/*
  Cart Billbuster: "ADDED" state for recommendation cards, the open/close
  toggle of the order summary, and confetti on milestone unlock.
  Loaded from layout/theme.liquid only when the Cart Billbuster setting is on.

  The theme's <add-to-cart> adds the product and re-renders the whole drawer
  (cart-drawer.js renderContents), so the card disappears at once because the
  product is now in the cart. This script only puts that card back in the same
  slot with an "ADDED" button for 2 seconds, then removes it. Adding to cart,
  tracking events and the loader are untouched.
*/
(() => {
  const ADDED_TEXT = 'ADDED';
  const ADDED_MS = 2000;
  let pending = null;

  // Remember which card was clicked, before the drawer is re-rendered.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('.bb-reco-card__atc');
      if (!button || !button.dataset.variantId || button.hasAttribute('disabled')) return;
      const card = button.closest('.bb-reco-card');
      const list = card && card.parentElement;
      if (!list) return;
      pending = {
        html: card.outerHTML,
        index: Array.prototype.indexOf.call(list.children, card),
      };
    },
    true,
  );

  document.addEventListener('ajaxProduct:error', () => {
    pending = null;
  });

  document.addEventListener('ajaxProduct:added', () => {
    if (!pending) return;
    const added = pending;
    pending = null;
    // renderContents() swaps the drawer HTML in a requestAnimationFrame that is
    // queued just before this event fires; this one runs right after it.
    requestAnimationFrame(() => showAdded(added));
  });

  // Order summary accordion: only toggles a class + aria-expanded; the CSS
  // grid-rows transition does the animation (no layout reads, INP-safe).
  document.addEventListener('click', (event) => {
    const header = event.target.closest('.bb-summary__header');
    if (!header) return;
    const summary = header.closest('.bb-summary');
    if (!summary) return;
    const isOpen = summary.classList.toggle('is-open');
    header.setAttribute('aria-expanded', String(isOpen));
  });

  function showAdded(added) {
    // No list = nothing left to recommend, so there is no slot to show it in.
    const list = document.querySelector('#mini-cart .bb-reco__list');
    if (!list) return;

    const template = document.createElement('template');
    template.innerHTML = added.html.trim();
    const card = template.content.firstElementChild;
    if (!card) return;

    card.classList.add('bb-reco-card--added');
    const button = card.querySelector('.bb-reco-card__atc');
    if (button) {
      button.textContent = ADDED_TEXT;
      button.removeAttribute('data-variant-id');
      button.setAttribute('disabled', '');
    }
    const loader = card.querySelector('.revamp-atc-loader');
    if (loader) loader.remove();

    list.insertBefore(card, list.children[added.index] || null);
    setTimeout(() => card.remove(), ADDED_MS);
  }
})();

/*
  Confetti when a milestone is unlocked.
  No DOM observer: it listens to the events the theme already fires after the
  drawer is re-rendered, and reads unlockedCount from the #billbuster-data
  block:
    ajaxProduct:added  add to cart (cart-drawer.js swaps the HTML in a
                       requestAnimationFrame queued just before this event)
    cart:updated       quantity change / remove (cart.js, after the swap)
    cartdrawer:opened  lazy first load of the drawer (sets the baseline only,
                       the drawer is closed then)
  Confetti fires only when the count goes up while the drawer is open. The
  last count is kept in sessionStorage so a page change does not replay it.
  The confetti library is loaded on the first unlock only (no cost on page load).
*/
(() => {
  // Browser build (sets window.confetti). Swap this URL to use another source.
  const CONFETTI_SRC = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
  const STORAGE_KEY = 'bb-unlocked-count';

  let lastData = null;
  let lastCount = readStored();
  let confettiLoad = null;

  document.addEventListener('ajaxProduct:added', () => requestAnimationFrame(check));
  document.addEventListener('cart:updated', check);
  document.addEventListener('cartdrawer:opened', check);

  function check() {
    const data = document.getElementById('billbuster-data');
    // Same block as last time = the drawer was not re-rendered.
    if (data === lastData) return;
    lastData = data;

    // No data block after a render = empty cart, nothing unlocked.
    let count = 0;
    if (data) {
      try {
        count = JSON.parse(data.textContent).unlockedCount || 0;
      } catch (e) {
        return;
      }
    }

    if (lastCount !== null && count > lastCount && isDrawerOpen()) fire();
    lastCount = count;
    writeStored(count);
  }

  function isDrawerOpen() {
    const details = document.querySelector('cart-drawer details');
    return !!details && details.hasAttribute('open');
  }

  function fire() {
    loadConfetti()
      .then((confetti) => {
        // Burst from the progress bar (the drawer sits on the right on desktop).
        const bar = document.querySelector('#mini-cart .bb-progress');
        const rect = bar ? bar.getBoundingClientRect() : null;
        const origin = rect
          ? {
              x: (rect.left + rect.width / 2) / window.innerWidth,
              y: (rect.top + rect.height / 2) / window.innerHeight,
            }
          : { x: 0.5, y: 0.3 };
        confetti({
          particleCount: 120,
          spread: 80,
          startVelocity: 35,
          origin,
          zIndex: 2147483000,
          disableForReducedMotion: true,
        });
      })
      .catch(() => {});
  }

  function loadConfetti() {
    if (window.confetti) return Promise.resolve(window.confetti);
    if (!confettiLoad) {
      confettiLoad = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = CONFETTI_SRC;
        script.async = true;
        script.onload = () => (window.confetti ? resolve(window.confetti) : reject());
        script.onerror = () => {
          confettiLoad = null;
          reject();
        };
        document.head.appendChild(script);
      });
    }
    return confettiLoad;
  }

  function readStored() {
    try {
      const value = window.sessionStorage.getItem(STORAGE_KEY);
      return value === null ? null : parseInt(value, 10);
    } catch (e) {
      return null;
    }
  }

  function writeStored(count) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, String(count));
    } catch (e) {}
  }
})();

/*
  Progress line into the next milestone: animates from where it was before
  the drawer re-render instead of jumping (Liquid renders the final value as
  an inline scaleX on .bb-step__fill). Same render events as the confetti.
    Same segment         previous fill -> new fill
    Moved up a milestone  0 -> new fill (the earlier line is now full green)
    Moved down            1 -> new fill (this line was full before)
  The first render after page load is shown as is, without animation.
*/
(() => {
  let lastData = null;
  let lastSegment = null;
  let lastFill = 0;

  document.addEventListener('ajaxProduct:added', () => requestAnimationFrame(update));
  document.addEventListener('cart:updated', update);
  document.addEventListener('cartdrawer:opened', update);

  function update() {
    const data = document.getElementById('billbuster-data');
    // Same data block as last time = the drawer was not re-rendered.
    if (data === lastData) return;
    lastData = data;

    const fill = document.querySelector('#mini-cart .bb-step__fill');
    let segment;
    let target;
    if (fill) {
      segment = parseFloat(fill.dataset.bbSegment);
      target = parseFloat(fill.dataset.bbFill);
    } else {
      // No partial line: everything unlocked (all lines full) or the cart is
      // below the first milestone / empty (no line to fill yet).
      let allUnlocked = false;
      try {
        allUnlocked = !!(data && JSON.parse(data.textContent).allUnlocked);
      } catch (e) {}
      segment = allUnlocked ? Infinity : 0;
      target = 0;
    }

    const previousSegment = lastSegment;
    const previousFill = lastFill;
    lastSegment = segment;
    lastFill = target;
    if (!fill || previousSegment === null) return;

    let start;
    if (segment === previousSegment) start = previousFill;
    else if (segment > previousSegment) start = 0;
    else start = 1;
    if (start === target) return;

    // Show the old value with no transition for one frame, then let the CSS
    // transition run to the new value. Transform only, no layout reads.
    fill.style.transition = 'none';
    fill.style.transform = `scaleX(${start})`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fill.style.transition = '';
        fill.style.transform = `scaleX(${target})`;
      });
    });
  }
})();
